// MOVIQ Shop the look — hotspots, maatkeuze en toevoegen aan de winkelwagen.
import { CartLinesUpdateEvent } from '@shopify/events';

if (!customElements.get('moviq-shop-the-look')) {
  class MoviqShopTheLook extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.querySelectorAll('.moviq-stl__dot').forEach((dot) => {
        dot.addEventListener('mouseenter', () => this.highlight(dot.dataset.target, false));
        dot.addEventListener('mouseleave', () => this.highlight(null, false));
      });
      this.querySelectorAll('.moviq-stl__row').forEach((row) => {
        row.addEventListener('mouseenter', () => this.highlight(row.dataset.row, false));
        row.addEventListener('mouseleave', () => this.highlight(null, false));
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick = (event) => {
      const size = event.target.closest('.moviq-stl__size');
      if (size && !size.disabled) return this.selectSize(size);

      const dot = event.target.closest('.moviq-stl__dot');
      if (dot) return this.highlight(dot.dataset.target, true);

      const single = event.target.closest('[data-add-single]');
      if (single) return this.addRow(single.closest('.moviq-stl__row'), single);

      const all = event.target.closest('[data-add-all]');
      if (all) return this.addAll(all);
    };

    selectSize(button) {
      const row = button.closest('.moviq-stl__row');
      row.querySelectorAll('.moviq-stl__size').forEach((b) => b.setAttribute('aria-pressed', b === button ? 'true' : 'false'));
      row.classList.remove('is-missing');
      row.querySelector('.moviq-stl__hint')?.setAttribute('hidden', '');
      row.dataset.price = button.dataset.price;
      row.dataset.compare = button.dataset.compare || button.dataset.price;
      this.updateTotal();
    }

    highlight(id, scroll) {
      this.querySelectorAll('.moviq-stl__row').forEach((r) => r.classList.toggle('is-active', r.dataset.row === id));
      this.querySelectorAll('.moviq-stl__dot').forEach((d) => d.classList.toggle('is-active', d.dataset.target === id));
      if (scroll && id) {
        const row = this.querySelector(`.moviq-stl__row[data-row="${id}"]`);
        row?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    selectedVariant(row) {
      return row.querySelector('.moviq-stl__size[aria-pressed="true"]')?.dataset.variantId || null;
    }

    updateTotal() {
      const el = this.querySelector('[data-look-total]');
      if (!el) return;
      let total = 0;
      let compare = 0;
      this.querySelectorAll('.moviq-stl__row[data-available="true"]').forEach((r) => {
        const price = Number(r.dataset.price) || 0;
        total += price;
        compare += Math.max(Number(r.dataset.compare) || 0, price);
      });
      el.textContent = this.formatMoney(total);
      const compareEl = this.querySelector('[data-look-compare]');
      if (compareEl) {
        compareEl.textContent = this.formatMoney(compare);
        compareEl.hidden = compare <= total;
      }
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
      const fmt = (value, decimals, thousands, decimal) => {
        const [whole, frac] = (value / 100).toFixed(decimals).split('.');
        const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return frac ? w + decimal + frac : w;
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        switch (key) {
          case 'amount_no_decimals': return fmt(cents, 0, ',', '.');
          case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
          case 'amount_with_period_and_comma_separator': return fmt(cents, 2, '.', ',');
          default: return fmt(cents, 2, ',', '.');
        }
      });
    }

    markMissing(row) {
      row.classList.add('is-missing');
      row.querySelector('.moviq-stl__hint')?.removeAttribute('hidden');
    }

    addRow(row, button) {
      const id = this.selectedVariant(row);
      if (!id) {
        this.markMissing(row);
        return;
      }
      this.addItems([{ id: Number(id), quantity: 1 }], button);
    }

    addAll(button) {
      const rows = [...this.querySelectorAll('.moviq-stl__row[data-available="true"]')];
      const items = [];
      let missing = null;
      rows.forEach((row) => {
        const id = this.selectedVariant(row);
        if (id) items.push({ id: Number(id), quantity: 1 });
        else {
          this.markMissing(row);
          missing ??= row;
        }
      });
      if (missing) {
        missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.showError('Kies voor elk item een maat om de complete look toe te voegen.');
        return;
      }
      this.addItems(items, button);
    }

    showError(message) {
      const el = this.querySelector('.moviq-stl__error');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    async refreshCart() {
      const cartItems = document.querySelector('cart-items-component');
      if (cartItems) {
        await customElements.whenDefined('cart-items-component');
        if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
      }
      const url = (window.Theme?.routes?.cart_url || '/cart') + '.js';
      const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      return res.json();
    }

    async addItems(items, button) {
      if (!items.length || button.disabled) return;
      this.showError('');
      button.disabled = true;
      button.classList.add('is-loading');

      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      const deferred = CartLinesUpdateEvent.createPromise();

      button.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: deferred.promise,
        })
      );

      try {
        const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds.join(',') }),
        });
        const data = await res.json();
        const cart = await this.refreshCart();
        const didError = Boolean(data.status);

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'moviq-shop-the-look',
            sourceId: this.id,
            itemCount: items.length,
            sections: didError ? undefined : data.sections,
            didError,
          },
        });

        if (didError) {
          this.showError(data.description || data.message || 'Toevoegen is niet gelukt. Probeer het opnieuw.');
        } else {
          button.classList.add('is-added');
          const live = this.querySelector('[data-live]');
          if (live) live.textContent = 'Toegevoegd aan je winkelwagen';
          setTimeout(() => button.classList.remove('is-added'), 2000);
        }
      } catch (error) {
        deferred.reject(error);
        this.showError('Toevoegen is niet gelukt. Probeer het opnieuw.');
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  }

  customElements.define('moviq-shop-the-look', MoviqShopTheLook);
}